// Who is in a space, and who sent a message — the Messages surface's identity
// layer (T-0200, design-crit packet 003 §1).
//
// The space thread and roster used to print raw handles ("sprout-demo.sage") as
// bylines, while the agents self-prefixed "🌿 Sage —" in the body — the emoji
// convention became a de-facto avatar. This blesses it: a readable NAME plus a
// member-CATEGORY glyph, with the exact handle kept for hover.
//
// ## Why a category glyph, not the agent's own 🌿
//
// The wire carries no agent-chosen avatar field. Inventing a per-agent emoji
// (hashing the handle to 🦊) would *contradict* the body's own "🌿 Sage —" and
// claim an identity the agent never chose. A category glyph — 🧑 you · 🤖 agent ·
// 🎩 the Representative — says only what KIND of member this is, which is
// provable from `trust_level` + the rep segment, and never fights the flourish
// the agent puts in its own text. A true per-agent avatar is a follow-up that
// needs a wire field (noted in the T-0200 PR).

import type { Message, SpaceMember } from './api';
import { identitySegment, isRepMember } from './orgRep';

/** Single source of the app version string (was inlined in App.tsx's header). */
export const APP_VERSION = '0.1.0';

export type MemberKind = 'you' | 'human' | 'agent' | 'rep';

/** Member-category glyphs. Keyed by kind, not identity — see the file header. */
export const MEMBER_GLYPH: Record<MemberKind, string> = {
  you: '🧑',
  human: '🧑',
  agent: '🤖',
  rep: '🎩',
};

/** The fallback glyph for a sender with no roster match (a departed member, or a
 *  DM where there is no roster to resolve against). Neutral: a person, no claim. */
export const UNKNOWN_GLYPH = '👤';

type MemberLike = Pick<SpaceMember, 'user_id' | 'persona' | 'trust_level'>;

/** What KIND of member this is, from provable signals only. */
export function memberKind(member: MemberLike, currentUserId?: string): MemberKind {
  if (currentUserId && member.user_id === currentUserId) return 'you';
  if (isRepMember(member)) return 'rep';
  if (member.trust_level === 'self_device') return 'human';
  return 'agent';
}

/** The category glyph for a member (convenience over memberKind + MEMBER_GLYPH). */
export function memberGlyphFor(member: MemberLike, currentUserId?: string): string {
  return MEMBER_GLYPH[memberKind(member, currentUserId)];
}

/** The honest role word for the members sheet — retires "Persistent (cross-human)". */
export function memberRoleLabel(member: SpaceMember, isSelf: boolean): string {
  if (isSelf) return 'you';
  if (isRepMember(member)) return 'the Representative';
  switch (member.trust_level) {
    case 'self_device':
      return 'your device';
    case 'cross_human_guest':
      return 'guest';
    case 'cross_human_persistent':
      return 'agent';
  }
}

/** Title-case a lone identity segment so "sage" reads as the name "Sage". */
function titleCaseSegment(seg: string): string {
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : seg;
}

/**
 * A readable name for a member: the explicit display name if it has one, else
 * the trailing identity segment of the handle, title-cased ("sprout-demo.sage"
 * → "Sage"). Never blank — falls back to the raw persona.
 */
export function memberName(
  member: Pick<SpaceMember, 'display_name' | 'persona'>,
): string {
  const explicit = member.display_name?.trim();
  if (explicit) return explicit;
  const seg = identitySegment(member.persona);
  return titleCaseSegment(seg) || member.persona.trim() || 'Unknown';
}

/**
 * The `{ name, handle }` a settings-drawer Agent row leads with (packet 007 / T-0230):
 * the display NAME first, the handle secondary — never the reverse.
 *
 *   { agent_suffix: 'rue',        display_name: 'sprout-demo.rue' }    → { name: 'Rue',                    handle: 'sprout-demo.rue' }
 *   { agent_suffix: 'sage',       display_name: 'Sage' }               → { name: 'Sage',                   handle: 'sage' }
 *   { agent_suffix: 'claude-prod',display_name: 'Claude (production)' } → { name: 'Claude (production)',    handle: 'claude-prod' }
 *   { agent_suffix: 'claude-dev', display_name: null }                 → { name: 'Claude-dev',             handle: 'claude-dev' }
 *
 * `memberName` can't be reused here: a mesh coworker's `display_name` is the QUALIFIED
 * handle itself ("sprout-demo.rue"), so `display_name || …` would lead with the handle —
 * the very bug T-0230 fixes. So `display_name` is treated as the handle-restated when it
 * is blank, is literally the segment ("rue"/"Rue"), or is a qualified handle whose
 * trailing segment is this agent; in every such case the NAME is the title-cased segment
 * and the handle keeps whatever qualified form it had.
 */
export function agentIdentity(agent: {
  agent_suffix: string;
  display_name: string | null;
}): { name: string; handle: string } {
  const seg = agent.agent_suffix;
  const dn = agent.display_name?.trim() ?? '';
  const isQualifiedHandle = /[/.]/.test(dn) && identitySegment(dn) === seg.toLowerCase();
  const dnIsHandle = dn === '' || dn.toLowerCase() === seg.toLowerCase() || isQualifiedHandle;
  return {
    name: dnIsHandle ? titleCaseSegment(seg) || seg : dn,
    handle: isQualifiedHandle ? dn : seg,
  };
}

export interface SenderIdentity {
  /** "you" | "Sage" | "Operator" — what the byline shows. */
  name: string;
  /** The member-category glyph that precedes the name. */
  glyph: string;
  /** The raw handle, kept for the hover/title so the exact address is never lost. */
  handle: string;
  isSelf: boolean;
}

/**
 * Resolve a message's sender to a byline identity, given the space roster and
 * the current user. With no roster (a DM), or no match (a departed member), it
 * degrades to the derived name + neutral glyph rather than guessing a category.
 */
export function resolveSender(
  message: Pick<Message, 'sender_user_id' | 'sender_persona'>,
  members: readonly SpaceMember[] | undefined,
  currentUserId: string | undefined,
): SenderIdentity {
  const handle = message.sender_persona.trim() || 'System';
  const isSelf = Boolean(currentUserId && message.sender_user_id === currentUserId);
  const member = members?.find(
    (m) =>
      m.user_id === message.sender_user_id ||
      m.persona === message.sender_persona,
  );

  if (isSelf) {
    return { name: 'you', glyph: MEMBER_GLYPH.you, handle, isSelf: true };
  }
  if (member) {
    const kind = memberKind(member, currentUserId);
    return { name: memberName(member), glyph: MEMBER_GLYPH[kind], handle, isSelf: false };
  }
  return {
    name: titleCaseSegment(identitySegment(handle)) || handle,
    glyph: UNKNOWN_GLYPH,
    handle,
    isSelf: false,
  };
}

/**
 * The space's title. Prefer its description; else derive from the roster
 * ("Sage, Pip +3"); else "Untitled space". The member-derived form is only
 * reachable where the roster is in hand (the header) — the sidebar, whose wire
 * carries no members, lands on "Untitled space".
 */
export function spaceDisplayName(
  description: string | null | undefined,
  members?: readonly SpaceMember[],
  currentUserId?: string,
): string {
  const explicit = description?.trim();
  if (explicit) return explicit;

  const others = (members ?? []).filter(
    (m) => !(currentUserId && m.user_id === currentUserId),
  );
  if (others.length > 0) {
    const names = others.map((m) => memberName(m));
    const shown = names.slice(0, 2).join(', ');
    const extra = names.length - 2;
    return extra > 0 ? `${shown} +${extra}` : shown;
  }
  return 'Untitled space';
}

/**
 * The title for a space ROW in a list, where the payload carries the space's `name`
 * (migration #31 added `rooms.name`; both `GET /api/spaces` and `GET /api/me/spaces` send it).
 *
 * The name is AUTHORITATIVE — it is what the desktop list shows — so it wins. `description`
 * is the legacy fallback (a pre-#31 row that only ever set a description), then the member
 * roster ("Sage, Pip +3"), then "Untitled space". This is the ONE resolver both lists share,
 * so the mobile row and the desktop row can never again read the a-team space two different
 * ways (T-0312: the mobile row was reading `description` alone and landing on "Untitled space"
 * for a named space whose description is empty).
 */
export function spaceListTitle(
  name: string | null | undefined,
  description: string | null | undefined,
  members?: readonly SpaceMember[],
  currentUserId?: string,
): string {
  const explicitName = name?.trim();
  if (explicitName) return explicitName;
  return spaceDisplayName(description, members, currentUserId);
}

export interface MembersSummary {
  count: number;
  /** "you, 5 agents & the Representative" — the parties, minus the "N members —". */
  parties: string;
  /** Up to `max` category glyphs, you first, for the avatar stack. */
  avatars: string[];
}

/**
 * Summarize a roster for the collapsed members row: a count, a plain-language
 * parties list, and an avatar stack of category glyphs.
 */
export function membersSummary(
  members: readonly SpaceMember[],
  currentUserId?: string,
  maxAvatars = 5,
): MembersSummary {
  const kinds = members.map((m) => ({ m, kind: memberKind(m, currentUserId) }));
  const hasYou = kinds.some((k) => k.kind === 'you');
  const agents = kinds.filter((k) => k.kind === 'agent').length;
  const guests = members.filter((m) => m.trust_level === 'cross_human_guest' && !isRepMember(m)).length;
  const reps = kinds.filter((k) => k.kind === 'rep').length;
  const humans = kinds.filter((k) => k.kind === 'human').length;

  const parts: string[] = [];
  if (hasYou) parts.push('you');
  if (humans > 0) parts.push(humans === 1 ? '1 person' : `${humans} people`);
  // Guests are agents' trust bucket too; count them plainly rather than as "agents".
  const trueAgents = agents - guests;
  if (trueAgents > 0) parts.push(trueAgents === 1 ? '1 agent' : `${trueAgents} agents`);
  if (guests > 0) parts.push(guests === 1 ? '1 guest' : `${guests} guests`);
  if (reps > 0) parts.push(reps === 1 ? 'the Representative' : `${reps} Representatives`);

  // "you, 5 agents & the Representative" — commas between, an ampersand before last.
  let parties: string;
  if (parts.length <= 1) {
    parties = parts[0] ?? '';
  } else {
    parties = `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;
  }

  // Avatar stack: you first, then the rest in roster order.
  const ordered = [...kinds].sort((a, b) => (a.kind === 'you' ? -1 : b.kind === 'you' ? 1 : 0));
  const avatars = ordered.slice(0, maxAvatars).map((k) => MEMBER_GLYPH[k.kind]);

  return { count: members.length, parties, avatars };
}
